import { createSearchParams } from "react-router-dom";
import { InputValidation } from "../models/InputValidation";
import { JobView } from "../models/JobView";

export interface PasswordValidationConfig {
    minLength: number;
    minCategories: number;
}

const defaultPasswordConfig: PasswordValidationConfig = { 
    minLength: 8, 
    minCategories: 2 
};

export interface PasswordRuleResult {
    hasMinChars: boolean;
    hasLower: boolean;
    hasUpper: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
    categoryCount: number;
    isValid: boolean;
    config: PasswordValidationConfig;
}

export function validateEmail(email: string): boolean {    
    const emailRegex = /^[a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-]?[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;    
    return emailRegex.test(email);
}

export function validateEmails(emails: string | undefined): boolean {
    if (!emails) return false; // or return true if empty string is valid
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const emailList = emails
        .split(',')
        .map(email => email.trim())
        .filter(email => email !== '');
    return emailList.every(email => emailRegex.test(email));
}

export const validateName = (name: string, value: string, maxLimit: number) : InputValidation => {
    let regex = /^[A-Za-z\u0D80-\u0DFF\u0B80-\u0BFF\s.]*$/;  
    let nameValidation: InputValidation = {valid: true}; 
    if(value === "") {
        nameValidation ={valid: false, invalid: true, invalidMessage: name + " is required"};
    }
    else if(value.length < 3 || value.length > maxLimit) { 
        nameValidation ={valid: false, invalid: true, invalidMessage: "Character length only allows between 3 - " + maxLimit};
    } 
    else if(!regex.test(value)) { 
        nameValidation ={valid: false, invalid: true, invalidMessage: "Symbols or numbers are not allowed"};
    }
    return nameValidation;
}

export const validateAddress = (address: string) => {
    let regex = /^.{5,200}$/;    
    return regex.test(address);
}

export const validateLettersOnlyText = (value: string) => {
    let regex = /^[A-Za-z\u0D80-\u0DFF\u0B80-\u0BFF\s]*$/;
    return regex.test(value);
}

export const validateNIC = (NIC: string) => {
    let regex = /^([0-9]{9}[x|X|v|V]|[0-9]{12})$/m;    
    return regex.test(NIC);
}

export const validateCV = (cv: File | undefined) : InputValidation => {        
    const validCVTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const validExtensions = ['.pdf', '.doc', '.docx'];
    const fileExtension = cv?.name.toLowerCase().split('.').pop();
    let cvValidation: InputValidation = {valid: true};  
    if(!cv) {
        cvValidation = {valid: false, invalid: true, invalidMessage: "CV is required"};
    } else if (!validCVTypes.includes(cv?.type) || !validExtensions.includes(`.${fileExtension}`)) {
        cvValidation = {valid: false, invalid: true, invalidMessage: "Invalid CV format. Try pdf, docx or doc format"};
    } else if (cv?.size > 2000000) {
        cvValidation = {valid: false, invalid: true, invalidMessage: "CV file size exceeds maximum allowable size (2 MB)"};
    } else if (cv?.size <= 0) {
        cvValidation = {valid: false, invalid: true, invalidMessage: "CV upload was not successful. Please try again"};
    }
    return cvValidation;
}

export const getCandidateCVURL = (translationText: string, isDraftCV: boolean = false, openInBrowser: boolean = false) => {
    let params: any = { translationText: translationText, isDraftCV: isDraftCV.toString() };
    if (openInBrowser) {
        params.openInBrowser = "true";
    }
    return getHost() + "api/candidate/cv?" + createSearchParams(params);
}

export const getCandidateProfilePictureURL = (candidateId: number, token: string) => {
    let profilePictureURL = "";
    if(candidateId){
        let params = { candidateId: candidateId.toString(), token: token.toString()};
        profilePictureURL = getHost() + "api/candidate/getProfilePicture?" + createSearchParams(params);
    }
    return profilePictureURL;
}

export const getRecruiterProfilePictureURL = (recruiterId: number) => {
    let profilePictureURL = "";
    if(recruiterId){
        let params = { recruiterId: recruiterId.toString() };
        profilePictureURL = getHost() + "api/recruiter/getProfilePicture?" + createSearchParams(params);
    }
    return profilePictureURL;
}

export const validatePhoneNumber = (number: string) => {
    //Valid numbers : 07********, 7********, +947********, +9407********, 947********, 
    let regex = /^(7|07|\+94[7]|94[7]|94[07]|\+94[07]?)[0-9]\d{7}$/;    
    return regex.test(number);
}

export const validateTelephoneNumber = (number: string) => {
    const regex = /^(?:\+94\d{9}|\+(?!94)\d{8,15}|94\d{9}|0\d{9}|7\d{8})$/;
    return regex.test(number);
}

export const getFormatedDate = (date: Date) => {
    if (date) {
        let formatedDate = new Date(date).toLocaleTimeString("en-us", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric"
          });
      return formatedDate.replace("at", " | ");
    }
};

export const formatDateWithSuperscript = (date: Date) => {
    const getOrdinalSuffix = (day: number): string => {
        if (day >= 11 && day <= 13) {
            return `${day}ᵗʰ`;
        }
        switch (day % 10) {
            case 1: return `${day}ˢᵗ`;
            case 2: return `${day}ⁿᵈ`;
            case 3: return `${day}ʳᵈ`;
            default: return `${day}ᵗʰ`;
        }
    };
    if (date) {
        let dateObject = new Date(date);
        const day = dateObject.getDate();
        const month = dateObject.toLocaleString("en-us", { month: "short" });
        const year = dateObject.getFullYear();
        const time = dateObject.toLocaleTimeString("en-us", {
            hour: "numeric",
            minute: "numeric",
            hour12: true
        });
        const formattedDate = `${getOrdinalSuffix(day)} ${month} ${year} | ${time}`;
        return formattedDate;
    }
};

export const validateImageFile = (image: File): InputValidation => {
    const validImageTypes = ['image/jpg', 'image/jpeg', 'image/png'];
    let imageValidation: InputValidation = { valid: true };
    if (!validImageTypes.includes(image?.type)) {
        imageValidation = { valid: false, invalid: true, invalidMessage: "Invalid Image format. Try jpg, jpeg or png format" };
    } else if (image?.size > 2000000) {
        imageValidation = { valid: false, invalid: true, invalidMessage: "File size exceeds maximum allowable size (2 MB)" };
    } else if (image?.size <= 0) {
        imageValidation = { valid: false, invalid: true, invalidMessage: "There is an issue with your file, please try a different one" };
    }
    return imageValidation;
}

export const validateLinkedInUrl = (linkedInUrl: string)  => {
    let regex = /^(https?:\/\/)?(www\.)?linkedin\.com\/[A-Za-z0-9_-]+(\/[A-Za-z0-9_-]+)*\/?$/;
    return regex.test(linkedInUrl);
}

export const getSEOKeyword = (text: string): string => {
    return text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')   // Remove special characters
        .replace(/\s+/g, '-')       // Replace spaces with hyphens
        .trim();                    // Trim any leading/trailing spaces
};

// slug → locationId (includes aliases)
export const LOCATION_SLUG_TO_ID: Record<string, number> = {
    "remote-jobs": 57,
    "work-from-home-jobs": 57,
    "island-wide-jobs": 59,
    "overseas-jobs": 54
};

export const createJobStructuredData = (jobView: JobView) => {
    const getValidValue = (val: any) => (val !== null && val !== undefined && val !== "" && val !== "-" && val !== "Any" ? val : undefined);
    const title = getValidValue(jobView.jobTitle) || "Position Available";
    const description = getValidValue(jobView.jobInfo) || "<p>Full job details are available on our website.</p>";
    const orgName = getValidValue(jobView.organization?.organizationName) || "XpressJobs Partner";
    const datePosted = jobView.createdDate ? new Date(jobView.createdDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    const expiryDate = jobView.jobItem?.expiryDateOnWebsite ? new Date(jobView.jobItem.expiryDateOnWebsite).toISOString().split('T')[0] : undefined;
    const type = getValidValue(jobView.jobItem?.jobType) || "FULL_TIME";
    const rawLocations = jobView.jobItem?.locations || "UnKnown";
    const locationArray = typeof rawLocations === 'string' ? rawLocations.replace(/^; /, '').split('; ') : ["Unknown"];
    const seoKeyword = getSEOKeyword(jobView.jobTitle + "-" + jobView.jobItem.organizationName);
    const structuredData = {
        "@context": "https://schema.org/",
        "@type": "JobPosting",
        "title": title,
        "description": description,
        "datePosted": datePosted,
        "validThrough": expiryDate,
        "employmentType": type,
        "directApply": true,
        "identifier": {
            "@type": "PropertyValue",
            "name": "XpressJobs",
            "value": getValidValue(jobView.jobId)
        },
        "hiringOrganization": {
            "@type": "Organization",
            "name": orgName,
            "logo": getValidValue(jobView.organization?.logoImageUrl),
            "url": getValidValue(jobView.organization?.website)
        },
       "jobLocation": locationArray.map(loc => ({
            "@type": "Place",
            "address": {
                "@type": "PostalAddress",
                "addressLocality": loc.split(',')[0].trim() || "Unknown",
                "addressCountry": !loc.includes("Foreign Job") ? "LK" : "Unknown"
            }
        })),
        "url": `https://xpress.jobs/jobs/view/${jobView.jobId}/${seoKeyword}`,
        ...(getValidValue(jobView.minSalary) && !isNaN(Number(jobView.minSalary)) && {
            "baseSalary": {
                "@type": "MonetaryAmount",
                "currency": "LKR",
                "value": {
                    "@type": "QuantitativeValue",
                    "minValue": Number(jobView.minSalary),
                    "maxValue": getValidValue(jobView.maxSalary) && !isNaN(Number(jobView.maxSalary)) ? Number(jobView.maxSalary) : Number(jobView.minSalary),
                    "unitText": "MONTH"
                }
            }
        }),
        ...(getValidValue(jobView.education) && {
            "educationRequirements": {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": getValidValue(jobView.education)
            }
        })
    };
    return structuredData; 
}

export const getJobDescriptionImageURL = (jobId: number) => {
    if (!jobId) return "";
    const params = { jobId: jobId.toString() };
    return getHost() + "api/vacancy/getJobDescriptionImage?" + createSearchParams(params);
};


export const getHost = () => {
     const host = window.location.protocol + "//" + window.location.host + "/";
    return host;
}

export function validatePasswordRules(password: string, config: PasswordValidationConfig = defaultPasswordConfig): PasswordRuleResult {
    const hasMinChars = password.length >= config.minLength;
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^a-zA-Z0-9]/.test(password);

    const categoryCount = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
    const isValid = hasMinChars && categoryCount >= config.minCategories;

    return {
        hasMinChars,
        hasLower,
        hasUpper,
        hasNumber,
        hasSpecial,
        categoryCount,
        isValid,
        config
    };
}

export type PasswordStrengthLabel = 'Weak' | 'Fair' | 'Good' | 'Strong';

export function getPasswordStrengthLabel(result: PasswordRuleResult): PasswordStrengthLabel {
    const { hasMinChars, categoryCount, config } = result;
    if (!hasMinChars || categoryCount <= config.minCategories - 2) return 'Weak';
    if (categoryCount === config.minCategories - 1) return 'Fair';
    if (categoryCount === config.minCategories) return 'Good';
    return 'Strong';
}

export const getCandidateCVUrlByRecruiter = (recruiterId: number, translationText: string, token: string, openInBrowser: boolean = false) => {
    let params: any = { recruiterId: recruiterId, translationText: translationText, token: token.toString() };
    if (openInBrowser) {
        params.openInBrowser = "true";
    }
    return getHost() + "api/recruiter/cv?" + createSearchParams(params);
}